import { API_PATHS, type Job } from '@uroute/shared';
import { Button, FormField, useToast } from '@uroute/ui';
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { haptic } from '../lib/haptics';
import { useOnline } from '../lib/offline';
import { makeItem, newId, submitItems, type QueueItem } from '../lib/queue';
import { invalidate } from '../lib/useQuery';
import { Sheet } from './Sheet';
import { SignaturePad, type SignaturePadHandle } from './SignaturePad';
import { Camera, ImageIcon, PenIcon, Trash, WifiOff } from './icons';

interface Photo {
  id: string;
  file: File;
  url: string;
}

const MAX_PHOTOS = 6;
const MAX_BYTES = 15 * 1024 * 1024;

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Shrink camera photos before they hit the queue/upload (fast on 4G). */
async function optimizePhoto(file: File): Promise<File> {
  try {
    if (!file.type.startsWith('image/')) return file;
    const bitmap = await createImageBitmap(file);
    const max = 1600;
    const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size < 400_000) {
      bitmap.close?.();
      return file;
    }
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close?.();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82));
    if (!blob) return file;
    const name = `${file.name.replace(/\.[^.]+$/, '') || 'delivery'}.jpg`;
    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file;
  }
}

export interface PodSheetProps {
  open: boolean;
  onClose: () => void;
  job: Job;
}

/**
 * Proof of delivery: camera/gallery evidence + recipient + time + notes +
 * optional signature. Submissions are queued FIRST (IndexedDB) so a lost
 * connection can never lose a completed delivery.
 */
export function PodSheet({ open, onClose, job }: PodSheetProps) {
  const toast = useToast();
  const online = useOnline();
  const sigRef = useRef<SignaturePadHandle>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);

  const [photos, setPhotos] = useState<Photo[]>([]);
  const [recipient, setRecipient] = useState(job.deliveryAddress.contactName ?? '');
  const [deliveredAt, setDeliveredAt] = useState(() => toLocalInput(new Date()));
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState<{ pct: number; label: string } | null>(null);

  /* Reset the sheet whenever it closes (and revoke preview URLs). */
  useEffect(() => {
    if (open) return;
    setPhotos((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.url));
      return [];
    });
    setError(null);
    setProgress(null);
    setSubmitting(false);
    setRecipient(job.deliveryAddress.contactName ?? '');
    setNotes('');
    setDeliveredAt(toLocalInput(new Date()));
  }, [open, job.deliveryAddress.contactName]);

  const addFiles = async (picked: File[]) => {
    if (picked.length === 0) return;
    setError(null);
    const next: Photo[] = [];
    for (const file of picked) {
      if (file.size > MAX_BYTES) {
        setError('Photos must be smaller than 15 MB.');
        continue;
      }
      const optimized = await optimizePhoto(file);
      next.push({ id: newId(), file: optimized, url: URL.createObjectURL(optimized) });
    }
    setPhotos((prev) => [...prev, ...next].slice(0, MAX_PHOTOS));
  };

  const onPick = (event: ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files ? Array.from(event.target.files) : [];
    event.target.value = '';
    void addFiles(picked);
  };

  const removePhoto = (id: string) => {
    setPhotos((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter((p) => p.id !== id);
    });
  };

  const submit = async () => {
    setError(null);
    const name = recipient.trim();
    if (!name) {
      setError('Add the name of the person who received the delivery.');
      haptic('error');
      return;
    }
    const when = new Date(deliveredAt);
    if (!deliveredAt || Number.isNaN(when.getTime())) {
      setError('Check the delivery time.');
      haptic('error');
      return;
    }
    const signatureBlob = (await sigRef.current?.toBlob()) ?? null;
    if (job.podRequired && photos.length === 0 && !signatureBlob) {
      setError('Add at least one delivery photo or a signature — proof of delivery is required.');
      haptic('error');
      return;
    }

    setSubmitting(true);
    setProgress(null);
    try {
      const meta: Record<string, string> = {
        jobId: job.id,
        recipientName: name,
        deliveredAt: when.toISOString(),
      };
      const noteText = notes.trim();
      if (noteText) meta.notes = noteText;

      const items: QueueItem[] = [];
      let previous: string | undefined;

      for (const photo of photos) {
        const item = makeItem({
          kind: 'proof',
          label: `Photo for ${job.externalRef}`,
          path: API_PATHS.proofs,
          jobId: job.id,
          fields: { ...meta, type: 'photo' },
          files: [{ field: 'file', name: photo.file.name, type: photo.file.type, blob: photo.file }],
          dependsOn: previous,
        });
        items.push(item);
        previous = item.id;
      }

      if (signatureBlob) {
        const item = makeItem({
          kind: 'proof',
          label: `Signature for ${job.externalRef}`,
          path: API_PATHS.proofs,
          jobId: job.id,
          fields: { ...meta, type: 'signature' },
          files: [{ field: 'file', name: 'signature.png', type: 'image/png', blob: signatureBlob }],
          dependsOn: previous,
        });
        items.push(item);
        previous = item.id;
      }

      items.push(
        makeItem({
          kind: 'status',
          label: `Complete ${job.externalRef}`,
          path: `${API_PATHS.jobs}/${job.id}/status`,
          jobId: job.id,
          json: { to: 'completed', proofId: previous ? '{{pred:id}}' : null },
          dependsOn: previous,
        }),
      );

      const result = await submitItems(items, (settled, total, label) =>
        setProgress({ pct: Math.round((settled / total) * 100), label }),
      );

      if (result.status === 'done') {
        toast.show({
          title: 'Delivery completed',
          description: `${job.externalRef} is marked as delivered.`,
          tone: 'success',
        });
        haptic('success');
        invalidate('me/jobs');
        invalidate(`job/${job.id}`);
        onClose();
        return;
      }
      if (result.status === 'queued') {
        toast.show({
          title: 'Proof saved on this device',
          description: 'It will sync automatically when you are back online.',
          tone: 'warning',
          duration: 6000,
        });
        haptic('light');
        invalidate('me/jobs');
        onClose();
        return;
      }
      setError(result.error);
      toast.show({
        title: 'Could not complete delivery',
        description: result.error,
        tone: 'danger',
        duration: 8000,
      });
      haptic('error');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Please try again.';
      setError(message);
      haptic('error');
    } finally {
      setSubmitting(false);
      setProgress(null);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={submitting ? () => undefined : onClose}
      title="Proof of delivery"
      testId="pod-sheet"
      size="lg"
      disableBackdropClose={submitting}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            data-testid="pod-submit"
            className="sheet__primary"
          >
            Save &amp; complete
          </Button>
        </>
      }
    >
      {!online ? (
        <p className="notice notice--warning" data-testid="pod-offline">
          <WifiOff size={16} />
          <span>
            You&rsquo;re offline — the proof stays on this device and completes the job as soon as
            you reconnect.
          </span>
        </p>
      ) : null}

      <section className="sheet__section">
        <h3 className="section-label">
          Evidence <span className="section-label__hint">required</span>
        </h3>
        <div className="pod__buttons">
          <Button
            variant="primary"
            onClick={() => cameraRef.current?.click()}
            data-testid="pod-take-photo"
          >
            <Camera size={16} />
            Take photo
          </Button>
          <Button variant="secondary" onClick={() => galleryRef.current?.click()} data-testid="pod-add-photos">
            <ImageIcon size={16} />
            Add photos
          </Button>
        </div>
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          aria-label="Take a delivery photo with the camera"
          data-testid="pod-photo-input"
          onChange={onPick}
        />
        <input
          ref={galleryRef}
          type="file"
          accept="image/*"
          multiple
          className="sr-only"
          aria-label="Choose delivery photos from the gallery"
          data-testid="pod-gallery-input"
          onChange={onPick}
        />

        {photos.length > 0 ? (
          <ul className="pod__thumbs" data-testid="pod-thumbs">
            {photos.map((photo, index) => (
              <li key={photo.id} className="pod__thumb">
                <img src={photo.url} alt={`Delivery photo ${index + 1}`} />
                <button
                  type="button"
                  className="pod__thumbremove"
                  onClick={() => removePhoto(photo.id)}
                  aria-label={`Remove photo ${index + 1}`}
                  data-testid="pod-remove-photo"
                >
                  <Trash size={14} />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      <section className="sheet__section">
        <h3 className="section-label">Recipient</h3>
        <div className="stack">
          <FormField label="Received by" required hint="Who signed for the delivery?">
            {({ id, describedBy }) => (
              <input
                id={id}
                aria-describedby={describedBy}
                className="u-input"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                autoComplete="name"
                placeholder="Full name"
                data-testid="pod-recipient"
              />
            )}
          </FormField>
          <FormField label="Delivered at" required>
            {({ id, describedBy }) => (
              <input
                id={id}
                aria-describedby={describedBy}
                className="u-input"
                type="datetime-local"
                value={deliveredAt}
                onChange={(e) => setDeliveredAt(e.target.value)}
                data-testid="pod-delivered-at"
              />
            )}
          </FormField>
          <FormField label="Notes" hint="Optional — gate codes, parking, damage notes">
            {({ id, describedBy }) => (
              <textarea
                id={id}
                aria-describedby={describedBy}
                className="u-input u-textarea"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="pod-notes"
              />
            )}
          </FormField>
        </div>
      </section>

      <section className="sheet__section">
        <h3 className="section-label">
          Signature <span className="section-label__hint">optional</span>
        </h3>
        <SignaturePad ref={sigRef} />
        <p className="hint">
          <PenIcon size={14} /> Draw with a finger, or skip it and send a photo instead.
        </p>
      </section>

      {error ? (
        <p className="form-error" role="alert" data-testid="pod-error">
          {error}
        </p>
      ) : null}

      {submitting && progress ? (
        <div className="progress" role="status" aria-live="polite">
          <div className="progress__track">
            <span className="progress__fill" style={{ width: `${progress.pct}%` }} />
          </div>
          <span className="progress__text u-data">{progress.pct}%</span>
          <span className="progress__label">{progress.label}</span>
        </div>
      ) : null}
    </Sheet>
  );
}
